import StatusIcon from "@components/StatusIcon"
import { formatAccessEndDate, SUPPORT_EMAIL } from "@helpers/helpCenterResult"
import {
  ArrowTopRightOnSquareIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline"
import { ReactNode } from "react"
import { Trans, useTranslation } from "react-i18next"

const APPLE_URL = "https://support.apple.com/118223"
const GOOGLE_URL = "https://support.google.com/googleplay/workflow/9813244"

/**
 * A help center outcome: a green check or a yellow warning, the title, and
 * the details under it, centered like the login pages' confirmation states.
 */
export function ResultView({
  children,
  icon,
  title,
}: {
  children: ReactNode
  icon?: "success" | "warning"
  title: string
}) {
  return (
    <div
      className="flex grow animate-fade-up flex-col justify-center gap-6"
      data-testid="help-center-result"
    >
      {icon && (
        <StatusIcon
          icon={icon === "success" ? CheckIcon : ExclamationTriangleIcon}
          tone={icon === "success" ? "green" : "yellow"}
        />
      )}

      <h1 className="vm-heading">{title}</h1>

      <div className="space-y-4 text-center text-[15px] leading-relaxed text-vm-muted">
        {children}
      </div>

      <SupportContact />
    </div>
  )
}

/** "Questions? Contact us at support@…", on every help center result page. */
function SupportContact() {
  return (
    <p
      className="text-center text-sm text-vm-muted"
      data-testid="help-center-support-contact"
    >
      <Trans
        components={{
          supportLink: (
            <a className="vm-link break-all" href={`mailto:${SUPPORT_EMAIL}`} />
          ),
        }}
        i18nKey="pages.helpCenter.shared.questionsContactUs"
        values={{ email: SUPPORT_EMAIL }}
      />
    </p>
  )
}

/** "You keep your Pro access until …", nothing when the date is unknown. */
export function AccessUntil({ date }: { date: string | null | undefined }) {
  const { i18n } = useTranslation()

  const formattedDate = formatAccessEndDate(date, i18n.language)

  if (!formattedDate) return null

  return (
    <p>
      <Trans
        components={{ bold: <span className="font-semibold text-vm-ink" /> }}
        i18nKey="pages.helpCenter.shared.accessUntil"
        values={{ date: formattedDate }}
      />
    </p>
  )
}

/** A store's help page, as a card row that opens in a new tab. */
export function StoreLink({ name, url }: { name?: string; url: string }) {
  return (
    <a
      className="vm-card vm-row text-left transition-colors hover:border-vm-primary/40"
      href={url}
      rel="noreferrer"
      target="_blank"
    >
      <span className="min-w-0 grow">
        {name && (
          <span className="block text-[15px] font-semibold text-vm-ink">
            {name}
          </span>
        )}
        <span className="block break-all text-sm text-vm-primary-text">
          {url}
        </span>
      </span>

      <ArrowTopRightOnSquareIcon
        aria-hidden="true"
        className="h-5 w-5 shrink-0 text-vm-muted"
      />
    </a>
  )
}

/** The App Store / Play Store help pages the user has to follow. */
export function StoreLinks({
  apple,
  google,
}: {
  apple: boolean
  google: boolean
}) {
  return (
    <div className="space-y-3">
      {google && <StoreLink name="Google" url={GOOGLE_URL} />}

      {apple && <StoreLink name="Apple" url={APPLE_URL} />}
    </div>
  )
}
